import {quantitySelectors} from '../features/quantity-selectors';
import slideDown from '../util/slide-down';
import slideUp from '../util/slide-up';
import hideElement from '../util/hide-element';
import showElement from '../util/show-element';
import {register} from '../vendor/theme-scripts/theme-sections';
import Media from '../media/media';
import {productFormSection} from '../features/product-form';

window.theme.variables = {
  productPageSticky: false,
  bpSmall: false,
  mediaQuerySmall: 750,
};

const selectors = {
  addToCart: '[data-add-to-cart]',
  priceWrapper: '[data-price-wrapper]',
  slideshow: '[data-product-slideshow]',
  productImage: '[data-product-image]',
  productJson: '[data-product-json]',
  form: '[data-product-form]',
  dropdown: '[data-single-option-selector]',
  thumbs: '[data-product-thumbs]',
  dataSectionId: 'data-section-id',
  dataTallLayout: 'data-tall-layout',
  dataStickyEnabled: 'data-sticky-enabled',
  dataCartBar: 'data-cart-bar',
  dataReviews: 'data-reviews',
  productPage: '.product__page',
  formWrapper: '.form__wrapper',
  accordionToggle: '.accordion-toggle',
  accordionIcon: '.accordion__icon',
  tabs: '.tabs',
  productTabsWrapper: '.productTabsWrapper',
  productAccordion: '.product-accordion',
  productTabs: '.product-tabs',
  tabsLi: 'ul.tabs > li',
  tabLink: '.tab-link',
  tabContent: '.tab-content',
  dataTab: 'data-tab',
  cartBar: '#cart-bar',
  selectTag: 'select',
  dataPosition: 'data-position',
  productSubmitAdd: '.product__submit__add',
  dataIndex: 'data-index',
  templateProduct: '#template-product',
  siteFooterWrapper: '.site-footer-wrapper',
  shopifyProductReviews: '#shopify-product-reviews',
  classCurrent: 'current',
  classSticky: 'is-sticky',
  classStickyHeader: 'with-sticky-header',
  classRotate: 'rotate-90',
  classCartBarVisible: 'cart-bar--is-visible',
  classSiteFooterPush: 'site-footer--push',
  headerSticky: '[data-header-sticky="sticky"]',
};

/**
 * Product section constructor.
 * @param {string} container - selector for the section container DOM element
 */
class Product {
  constructor(section) {
    this.section = section;
    this.container = section.container;
    this.id = this.container.getAttribute(selectors.dataSectionId);
    this.tallLayout = this.container.getAttribute(selectors.dataTallLayout) === 'true';
    this.stickyEnabled = this.container.getAttribute(selectors.dataStickyEnabled) === 'true';
    this.headerSticky = document.querySelector(selectors.headerSticky) !== null;
    this.showReviews = this.container.getAttribute(selectors.dataReviews) === 'true';
    this.thumbs = this.container.querySelector(selectors.thumbs);

    // Stop parsing if we don't have the product json script tag when loading
    // section in the Theme Editor
    const productJson = this.container.querySelector(selectors.productJson);
    if ((productJson && !productJson.innerHTML) || !productJson) {
      return;
    }

    this.form = this.container.querySelector(selectors.form);

    this.init();
    this.tabs();
    this.tabsSpaceCheck();
    quantitySelectors();

    this.accordion();
    if (this.stickyEnabled) {
      this.stickyScroll();
    }
    if (this.container.getAttribute(selectors.dataCartBar) === 'true') {
      this.initCartBar();
    }
    const currentScope = this;
    window.addEventListener('resize', currentScope.tabsSpaceCheck);

    const reviewsAppInstalled = typeof window.SPR == 'function';

    if (this.showReviews && reviewsAppInstalled) {
      window.SPR.initDomEls();
      window.SPR.loadBadges();
    }
  }

  init() {
    theme.mediaInstances[this.id] = new Media(this.section);
    theme.mediaInstances[this.id].init();
  }

  stickyScroll() {
    this.stickyScrollCheck();

    document.addEventListener('theme:resize', () => this.stickyScrollCheck());
  }

  stickyScrollCheck() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const isDesktop = windowWidth >= theme.variables.mediaQuerySmall;
    const form = this.container.querySelector(selectors.formWrapper);
    const targetFormWrapper = this.container.querySelector(`${selectors.productPage} ${selectors.formWrapper}`);

    let noReviews = true;
    if (this.showReviews) {
      noReviews = false;
    }

    if (isDesktop) {
      const slideshow = this.container.querySelector(selectors.slideshow);
      if (!form || !slideshow) return;
      const productCopyHeight = form.offsetHeight;
      const productImagesHeight = slideshow.offsetHeight;

      // Is the product description and form taller than window space
      // Is is also shorter than the window and images
      if (productCopyHeight < productImagesHeight && productCopyHeight < window.innerHeight && noReviews) {
        theme.variables.productPageSticky = true;

        targetFormWrapper.classList.add(selectors.classSticky);
        if (this.headerSticky) {
          targetFormWrapper.classList.add(selectors.classStickyHeader);
        }
      } else {
        theme.variables.productPageSticky = false;
        targetFormWrapper.classList.remove(selectors.classSticky);
      }
    } else {
      targetFormWrapper.classList.remove(selectors.classSticky);
    }
  }

  tabs() {
    const ctx = this.container;
    const tabsNavList = ctx.querySelectorAll(selectors.tabsLi);
    const firstTabLink = ctx.querySelector(`${selectors.tabLink}-0`);
    const firstTabContent = ctx.querySelector(`${selectors.tabContent}-0`);

    if (firstTabContent) {
      firstTabContent.classList.add(selectors.classCurrent);
    }

    if (firstTabLink) {
      firstTabLink.classList.add(selectors.classCurrent);
    }

    if (tabsNavList.length) {
      const tabFunctionality = (element) => {
        const tabId = parseInt(element.getAttribute(selectors.dataTab));
        ctx.querySelector(`${selectors.tabsLi}.${selectors.classCurrent}`).classList.remove(selectors.classCurrent);
        ctx.querySelector(`${selectors.tabContent}.${selectors.classCurrent}`).classList.remove(selectors.classCurrent);

        element.classList.add(selectors.classCurrent);
        ctx.querySelector(`${selectors.tabContent}-${tabId}`).classList.add(selectors.classCurrent);
      };

      tabsNavList.forEach((element) => {
        element.addEventListener('click', function (e) {
          tabFunctionality(element);
        });

        element.addEventListener('keyup', function (e) {
          if (e.type === 'keyup' && e.keyCode !== 13) return;
          tabFunctionality(element);
        });
      });
    }
  }

  accordion() {
    // mobile accordion for tabs content
    const accordionToggles = this.container.querySelectorAll(selectors.accordionToggle);
    if (accordionToggles.length) {
      const self = this;

      accordionToggles.forEach((element) => {
        element.addEventListener('click', function (e) {
          const elemSibling = this.nextElementSibling;
          const checkNextDiv = elemSibling && elemSibling.tagName === 'DIV';
          if (checkNextDiv && window.getComputedStyle(elemSibling).display !== 'none') {
            slideUp(elemSibling);
          } else if (checkNextDiv) {
            slideDown(elemSibling);
            self.stickyScrollCheck;
          }

          const elemIcon = this.querySelector(selectors.accordionIcon);

          if (elemIcon) {
            elemIcon.classList.toggle(selectors.classRotate);
          }
        });
      });
    }
  }

  tabsSpaceCheck() {
    if (this.container && this.container.querySelector(selectors.tabs) && this.container.querySelector(selectors.productTabsWrapper)) {
      const tabsWidth = this.container.querySelector(selectors.tabs).clientWidth;
      const tabsWrapper = this.container.querySelector(selectors.productTabsWrapper).clientWidth - 31;
      const productAccordion = this.container.querySelector(selectors.productAccordion);
      const productTabs = this.container.querySelector(selectors.productTabs);
      if (tabsWidth >= tabsWrapper) {
        if (productTabs) {
          productTabs.style.position = 'absolute';
          productTabs.style.left = '-9999px';
          productTabs.style.display = 'table';
        }

        if (productAccordion) {
          showElement(productAccordion);
        }
      } else {
        if (productTabs) {
          productTabs.removeAttribute('style');
        }

        if (productAccordion) {
          hideElement(productAccordion);
        }
      }
    }
  }

  initCartBar() {
    const instance = this;
    const cartBar = document.querySelector(selectors.cartBar);
    const cartBarSelectors = cartBar.querySelectorAll(selectors.selectTag);
    const formSelectors = this.form.querySelectorAll(selectors.dropdown);
    const cartBarPosition = cartBar.getAttribute(selectors.dataPosition);

    if (cartBarSelectors.length) {
      cartBarSelectors.forEach((element) => {
        // Update product form on cart bar variant change
        element.addEventListener('change', function () {
          const index = this.getAttribute(selectors.dataIndex);
          const optionSelected = this.value;
          const targets = instance.form.querySelectorAll(`${selectors.dropdown}[${selectors.dataIndex}="${index}"]`);

          if (targets[0].tagName === 'INPUT') {
            for (const target of targets) {
              const targetIndex = target.getAttribute(selectors.dataIndex);
              const targetValue = target.value;
              if (targetIndex === index && targetValue === optionSelected) {
                target.checked = true;
                target.dispatchEvent(new Event('change'));
                break;
              }
            }
          } else {
            const select = targets[0];
            select.value = optionSelected;

            select.dispatchEvent(new Event('change'));
          }
        });
      });
    }

    // Update cart bar on product form variant change
    if (formSelectors.length) {
      formSelectors.forEach((element) => {
        element.addEventListener('change', function () {
          const index = this.getAttribute(selectors.dataIndex);
          const optionSelected = this.value;

          cartBar.querySelector(`[${selectors.dataIndex}="${index}"]`).value = optionSelected;
        });
      });
    }

    this.cartBar = cartBar;

    document.addEventListener('theme:scroll', this.scrollTop.bind(this));

    if (cartBarPosition === 'top') {
      this.setCartBarPosition();
      document.addEventListener('theme:scroll', this.setCartBarPosition.bind(this));
    }
  }

  scrollTop() {
    const scrolled = window.pageYOffset;
    const siteFooter = document.querySelector(selectors.siteFooterWrapper);

    if (this.form && this.cartBar) {
      const formOffset = this.form.offsetTop;
      const formHeight = this.form.offsetHeight;

      if (scrolled > formOffset + formHeight) {
        this.cartBar.classList.add(selectors.classCartBarVisible);
        siteFooter.classList.add(selectors.classSiteFooterPush);
      } else {
        this.cartBar.classList.remove(selectors.classCartBarVisible);
        siteFooter.classList.remove(selectors.classSiteFooterPush);
      }
    }
  }

  setCartBarPosition() {
    if (this.headerSticky) {
      this.cartBar.style.top = `var(--menu-height)`;
    } else {
      this.cartBar.style.top = '0';
    }
  }
}

const productSection = {
  onLoad() {
    this.section = new Product(this);
  },
  onUnload: function () {
    if (typeof this.section.unload === 'function') {
      this.section.unload();
    }
  },
};

register('product', [productFormSection, productSection]);
